import React from "react";
import { Button, Chip, Typography } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { money, needsReceiptReview, orderTotal, stockState, toolsDate } from "./toolsData";
import { MarketStatus } from "./ToolsLayout";

export function ProductActions({ product, onStock, onDelete }) {
  return (
    <div className="tools-actions">
      <Button
        size="small"
        variant="outlined"
        onClick={() => onStock(product)}
        disabled={stockState(product.stock) === "unknown"}
      >
        Update stock
      </Button>
      <Button
        size="small"
        component={Link}
        to={`/home/${encodeURIComponent(product._id)}/edit-tool`}
      >
        Edit
      </Button>
      <Button
        size="small"
        component={Link}
        to={`/home/${encodeURIComponent(product._id)}/view-tool`}
      >
        View
      </Button>
      <Button
        size="small"
        aria-label={`Delete ${product.name}`}
        onClick={() => onDelete(product)}
        sx={{ minWidth: 30 }}
      >
        <DeleteOutline fontSize="small" />
      </Button>
    </div>
  );
}
export function ProductCard({ product, onStock, onDelete }) {
  const state = stockState(product.stock);
  return (
    <article className="tools-mobile-card">
      <div className="tools-product">
        {(product.images?.[0] || product.imageUrl) && (
          <img src={product.images?.[0] || product.imageUrl} alt="" />
        )}
        <div>
          <Typography fontWeight={800}>{product.name}</Typography>
          <small>{product.category || "Uncategorised"}</small>
        </div>
      </div>
      <div className="tools-mobile-details">
        <Typography variant="body2" fontWeight={800}>
          {money(product.sellingPrice)}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={state === "low" ? "warning" : state === "out" ? "error" : "default"}
          label={
            state === "unknown"
              ? "Stock unavailable"
              : `${product.stock} units${state === "out" ? " · Out of stock" : state === "low" ? " · Low stock" : ""}`
          }
        />
      </div>
      <ProductActions product={product} onStock={onStock} onDelete={onDelete} />
    </article>
  );
}
export function OrderCard({ order, compact = false }) {
  return (
    <article className="tools-mobile-card">
      <div className="tools-mobile-details">
        <Typography variant="body2" fontWeight={800}>
          {order.orderId}
        </Typography>
        <Typography variant="body2" fontWeight={800}>
          {money(orderTotal(order))}
        </Typography>
      </div>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {order.shippingAddress?.fullName || "Customer unavailable"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {toolsDate(order.createdOn)}
        {!compact && ` · ${order.orderSummary?.itemCount ?? order.items?.length ?? "—"} items`}
      </Typography>
      <div className="tools-actions" style={{ margin: "12px 0" }}>
        <MarketStatus status={order.orderStatus} />
        {!compact && <MarketStatus status={order.paymentStatus} payment />}
      </div>
      <Button
        size="small"
        variant={order.orderStatus === "pending" ? "contained" : "outlined"}
        component={Link}
        to={`/home/tools-order/${encodeURIComponent(order.orderId)}`}
      >
        {order.orderStatus === "pending" ? "Review order" : "View order"}
      </Button>
    </article>
  );
}
export function PaymentCard({ record, onReview }) {
  return (
    <article className="tools-mobile-card">
      <Button
        size="small"
        component={Link}
        to={`/home/tools-order/${encodeURIComponent(record.orderId)}`}
      >
        {record.orderId}
      </Button>
      <Typography variant="caption" display="block" color="text.secondary">
        {record.paymentMethod || "Unspecified payment method"}
      </Typography>
      <div className="tools-actions" style={{ margin: "12px 0" }}>
        <MarketStatus status={record.paymentStatus} payment />
        <MarketStatus status={record.orderStatus} />
      </div>
      <Typography variant="body2">{record.transactionId || "No transaction reference"}</Typography>
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
        {toolsDate(record.paymentDate)}
      </Typography>
      <div className="tools-actions">
        {record.receiptUrl ? (
          <Button size="small" href={record.receiptUrl} target="_blank" rel="noopener noreferrer">
            Open receipt
          </Button>
        ) : (
          <Typography variant="caption">No file uploaded</Typography>
        )}
        {["pending", "processing"].includes(record.paymentStatus) ? (
          <Button
            size="small"
            variant={needsReceiptReview(record) ? "contained" : "outlined"}
            onClick={() => onReview(record.orderId)}
          >
            {record.receiptUrl ? "Review payment" : "Record payment"}
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={`/home/tools-order/${encodeURIComponent(record.orderId)}`}
          >
            View details
          </Button>
        )}
      </div>
    </article>
  );
}
export function OrderItemCard({ item }) {
  return (
    <article className="tools-mobile-card">
      <div className="tools-product">
        {item.images?.[0] && <img src={item.images[0]} alt="" />}
        <Typography variant="body2" fontWeight={800}>
          {item.name || "Product unavailable"}
        </Typography>
      </div>
      <div className="tools-mobile-details">
        <Typography variant="body2" color="text.secondary">
          {item.quantity ?? "—"} × {money(item.price)}
        </Typography>
        <Typography variant="body2" fontWeight={800}>
          {money(item.itemTotal)}
        </Typography>
      </div>
      <Typography variant="caption" color="text.secondary">
        GST: {item.gst === null || item.gst === undefined ? "—" : `${item.gst}%`}
      </Typography>
    </article>
  );
}
